import { LitElement, html } from 'lit-element';
import { connect } from '../core/connect';
import { withProduct } from '../core/resolveProperties';
import { safeProductId } from '../core/utils';
import { INCENTIVE_STANDARD_TYPES } from '../core/constants';

export class DiscountAmount {
  constructor(value) {
    this.value = value;
    this.className = 'DiscountAmount';
  }

  toString() {
    const value = Number(this.value);
    return Number.isInteger(value) ? `${value}` : value.toFixed(2);
  }
}

class DiscountPercent extends DiscountAmount {
  constructor(value) {
    super(value);
    this.className = 'DiscountPercent';
  }

  toString() {
    return `${this.value}%`;
  }
}

class ShippingDiscountPercent extends DiscountPercent {
  constructor(value) {
    super(value);
    this.className = 'ShippingDiscountPercent';
  }

  toString() {
    return this.value === 100 ? 'free shipping' : super.toString();
  }
}

const DISCOUNT_PERCENT = 'Discount Percent';
const DISCOUNT_AMOUNT = 'Discount Amount';
const TOTAL_PRICE = 'total_price';
const SHIPPING_TOTAL = 'shipping_total';
const SUB_TOTAL = 'sub_total';

const discountBuilder = ({ field, object, type, value }) => {
  const supportedDiscounts = [
    [new DiscountPercent(value), { field: TOTAL_PRICE, object: 'item', type: DISCOUNT_PERCENT }],
    [new DiscountAmount(value), { field: TOTAL_PRICE, object: 'item', type: DISCOUNT_AMOUNT }],
    [new ShippingDiscountPercent(value), { field: SHIPPING_TOTAL, object: 'order', type: DISCOUNT_PERCENT }],
    [new DiscountAmount(value), { field: SHIPPING_TOTAL, object: 'order', type: DISCOUNT_AMOUNT }],
    [new DiscountPercent(value), { field: SUB_TOTAL, object: 'order', type: DISCOUNT_PERCENT }],
    [new DiscountAmount(value), { field: SUB_TOTAL, object: 'order', type: DISCOUNT_AMOUNT }]
  ];

  const available = supportedDiscounts.find(([, it]) => it.field === field && it.object === object && it.type === type);
  return available && available[0];
};

export const getTransformedDiscounts = discounts => {
  return discounts.map(discount => discountBuilder(discount)).filter(discount => discount !== undefined);
};

function isMatchingIncentive(incentive, { incentiveValue, incentiveClass }) {
  if (discountBuilder(incentive).className !== incentiveClass) {
    return false;
  }

  if (incentiveValue && incentiveValue.toString() !== incentive.value.toString()) {
    return false;
  }

  return true;
}

export function replaceText(acc, curr) {
  return acc.replace(this.textToReplace, discountBuilder(curr));
}

const preferredIncentiveStandards = [INCENTIVE_STANDARD_TYPES.PSI, INCENTIVE_STANDARD_TYPES.PROGRAM_WIDE];

function compareThresholds(a, b) {
  return Number(a.threshold_value) - Number(b.threshold_value);
}

export class IncentiveText extends withProduct(LitElement) {
  static get properties() {
    return {
      ...super.properties,
      incentives: { type: Object, attribute: false },
      from: { type: String },
      label: { type: String },
      initial: { type: Boolean, default: false },
      value: { type: Number }
    };
  }

  createRenderRoot() {
    return this;
  }

  render() {
    const incentiveClass = this.from;
    const incentiveValue = this.value;
    const incentiveType = this.initial ? 'initial' : 'ongoing';
    /** @type {import('../core/types/reducer').Incentive[]} */
    const incentivesOfType = this.incentives[incentiveType] || [];

    // prefer to display PSI or program wide incentives, if available
    // since the incentives response may contain other incentives that do not apply, e.g. prepaid, Nth order, customer group, etc.
    const preferredIncentives = incentivesOfType.filter(
      incentive =>
        // we only have criteria if the incentive is standardized
        incentive.criteria &&
        incentive.criteria.node_type === 'PREMISE' &&
        preferredIncentiveStandards.includes(incentive.criteria.standard)
    );

    // prefer to show non-threshold discounts
    // we don't currently evaluate thresholds, so we don't know if they apply or not (especially if it's order-level)
    const preferredWithoutThreshold = preferredIncentives.filter(incentive => !incentive.threshold_field);
    // lowest threshold first, since thresholds are progressive and it's the discount the customer is most likely to get
    const preferredWithThreshold = preferredIncentives
      .filter(incentive => incentive.threshold_field)
      .sort(compareThresholds);

    const incentive = [
      // put preferred incentives first, so we return one of those if it matches
      ...preferredWithoutThreshold,
      // a thresholded PSI or program wide incentive is still better than an incentive that may not apply to this customer
      ...preferredWithThreshold,
      // if no preferred incentives matched, try the rest
      ...incentivesOfType.filter(incentive => !preferredIncentives.includes(incentive))
    ].find(incentive => isMatchingIncentive(incentive, { incentiveClass, incentiveValue }));

    return html`
      ${this.label} ${incentive ? discountBuilder(incentive) : this.renderFallback()}
    `;
  }

  renderFallback() {
    return html`
      ${discountBuilder({
        field: 'sub_total',
        object: 'order',
        type: 'Discount Percent',
        value: this.value
      })}
    `;
  }
}

export const mapStateToProps = (state, ownProps) => ({
  incentives: (state.incentives || {})[ownProps && ownProps?.product && safeProductId(ownProps?.product?.id)] || {}
});

export const ConnectedIncentiveText = connect(mapStateToProps)(IncentiveText);

export default ConnectedIncentiveText;
